import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";

export const Route = createFileRoute("/admin/users")({ component: UsersAdmin });

const adminRouteApi = getRouteApi("/admin");


function UsersAdmin() {
  const qc = useQueryClient();
  const { role } = adminRouteApi.useRouteContext();
  const isSubAdmin = role === "sub_admin";
  const [qInput, setQInput] = useState("");

  const [q, setQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQ(qInput), 250);
    return () => clearTimeout(id);
  }, [qInput]);
  const { data } = useQuery({
    queryKey: ["all_users"],
    queryFn: async () =>
      (await supabase
        .from("profiles")
        .select("id,name,phone,email,wallet_balance,winning_balance,referral_code,status,created_at")
        .order("created_at", { ascending: false })
        .limit(500)).data ?? [],
  });

  const [editing, setEditing] = useState<any>(null);
  const [delta, setDelta] = useState(0);
  const [note, setNote] = useState("");
  const [contactSource, setContactSource] = useState<"telegram" | "whatsapp" | "other_manual">("telegram");
  const [reference, setReference] = useState("");
  const [confirmingCredit, setConfirmingCredit] = useState(false);
  const [busy, setBusy] = useState(false);

  async function toggleBlock(u: any) {
    const newStatus = u.status === "blocked" ? "active" : "blocked";
    const { error } = await supabase.from("profiles").update({ status: newStatus }).eq("id", u.id);
    if (error) return toast.error(error.message);
    toast.success(`User ${newStatus}`);
    qc.invalidateQueries();
  }

  async function adjustBalance() {
    if (!editing || !delta) return;
    if (isSubAdmin && delta < 0) return toast.error("Sub-admins can only add balance, not deduct");
    if ((note ?? "").trim().length < 3) return toast.error("Reason (min 3 chars) is required");
    setBusy(true);
    const { error } = await supabase.rpc("admin_adjust_wallet" as any, {
      _user_id: editing.id,
      _delta: Number(delta),
      _note: note.trim(),
      _source: contactSource,
      _reference: reference.trim() || null,
    } as any);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Balance updated");
    setEditing(null); setDelta(0); setNote(""); setReference(""); setContactSource("telegram"); setConfirmingCredit(false);
    qc.invalidateQueries();
  }



  const filtered = (data ?? []).filter((u: any) =>
    !q || [u.name, u.phone, u.email, u.referral_code].some((f) => String(f ?? "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-black">Users</h1>
        <div className="relative w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search…" className="pl-9" />
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl bg-card shadow-card">
        <table data-sticky-col className="w-full min-w-[720px] text-sm whitespace-nowrap">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Balance</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map((u: any) => {
              const main = Number(u.wallet_balance ?? 0);
              const winning = Number(u.winning_balance ?? 0);
              const total = main + winning;
              return (
              <tr key={u.id} className="border-t">
                <td className="px-4 py-3 font-semibold">{u.name ?? "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.phone ?? u.email ?? "—"}</td>
                <td className="px-4 py-3">
                  <div className="font-bold">₹{total.toLocaleString("en-IN")}</div>
                  <div className="text-[10px] leading-tight text-muted-foreground">
                    Main: ₹{main.toLocaleString("en-IN")}<br />
                    Withdrawable: ₹{winning.toLocaleString("en-IN")}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{u.referral_code}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${u.status === "blocked" ? "bg-destructive/15 text-destructive" : "bg-success/15 text-success"}`}>{u.status ?? "active"}</span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(u); setDelta(0); setNote(""); }} className="rounded bg-primary px-2 py-1 text-[10px] font-bold text-primary-foreground">
                      {isSubAdmin ? "Add Balance" : "Wallet"}
                    </button>
                    {!isSubAdmin && (
                      <button onClick={() => toggleBlock(u)} className={`rounded px-2 py-1 text-[10px] font-bold ${u.status === "blocked" ? "bg-success text-success-foreground" : "bg-destructive text-destructive-foreground"}`}>
                        {u.status === "blocked" ? "Unblock" : "Block"}
                      </button>
                    )}
                  </div>
                </td>

              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setConfirmingCredit(false); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust Wallet — {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Current balance: <span className="font-bold">₹{Number(editing?.wallet_balance ?? 0).toLocaleString("en-IN")}</span></p>
            <div><Label>{isSubAdmin ? "Amount to add" : "Amount (use negative to deduct)"}</Label>
              <Input type="number" min={isSubAdmin ? 1 : undefined} value={delta} onChange={(e) => setDelta(Number(e.target.value))} />
            </div>

            <div>
              <Label>Contact Source</Label>
              <div className="mt-1 grid grid-cols-3 gap-1">
                {(["telegram","whatsapp","other_manual"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setContactSource(s)}
                    className={`rounded-lg px-2 py-1.5 text-xs font-bold uppercase ${contactSource === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                  >
                    {s === "other_manual" ? "Other" : s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Payment Reference / UTR (optional)</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / Ref ID (optional)" />
            </div>
            <div><Label>Reason (required, min 3 chars)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Deposit verified on Telegram" /></div>
            {!confirmingCredit ? (
              <Button
                onClick={() => {
                  if (!delta) return toast.error("Enter amount");
                  if ((note ?? "").trim().length < 3) return toast.error("Reason (min 3 chars) is required");
                  setConfirmingCredit(true);
                }}
                className="w-full gradient-primary"
              >
                Review & Apply
              </Button>
            ) : (
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2">
                <p className="text-xs font-semibold">Confirm this {delta > 0 ? "CREDIT" : "DEBIT"}:</p>
                <p className="text-sm">
                  {delta > 0 ? "Add" : "Deduct"} <span className="font-black">₹{Math.abs(Number(delta)).toLocaleString("en-IN")}</span>
                  {delta > 0 ? " to " : " from "}
                  <span className="font-semibold">{editing?.name}</span> ({editing?.phone})
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Source: {contactSource === "other_manual" ? "Other Manual" : contactSource} · Ref: {reference.trim() || "—"}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setConfirmingCredit(false)} disabled={busy}>Back</Button>
                  <Button className="flex-1 gradient-primary" onClick={adjustBalance} disabled={busy}>
                    {busy ? "Processing…" : "Confirm"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
